'use client';

import { ReactNode, useEffect, useState } from 'react';

import { CameraCaptureAuto } from '@northladder/design-system';
import { useTranslations } from '@northladder/i18n';

import { CameraFailedError } from './CameraFailedError';

interface ICameraCircularMaskSVGProps {
  height: number;
  width: number;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a circular mask on top of the camera to provide an areas of
 * focus for the user.
 */
function CameraCircularMaskSVG({ height, width }: ICameraCircularMaskSVGProps) {
  return (
    <svg
      className="absolute bottom-0 left-0 right-0 top-0 mt-[-40px] block h-screen w-screen"
      id="cameraMaskSVG"
      viewBox={`0 0 ${width} ${height}`}
    >
      <defs>
        <mask height="100%" id="overlay-mask" width="100%" x="0" y="0">
          <rect fill="#fff" height="100%" width="100%" x="0" y="0" />
          <circle cx="50%" cy="40%" r="170" />
        </mask>
      </defs>
      <rect
        className="fill-black/90"
        fillOpacity="1"
        height={height}
        mask="url(#overlay-mask)"
        width={width}
        x="0"
        y="0"
      />
    </svg>
  );
}

interface IBackCameraTestViewProps {
  children: ReactNode;
  onCaptureSuccess: () => void;
}

/**
 * -----------------------------------------------------------------------------
 * Sets up a simple camera view for testing the back camera, which includes an
 * overlay to highlight important areas.
 */
export function BackCameraTestView({
  children,
  onCaptureSuccess,
}: IBackCameraTestViewProps) {
  const t = useTranslations('BackCameraEvaluate.cameraErrors');

  const [hasAccessError, setHasAccessError] = useState(false);
  const [svgDimensions, setSvgDimensions] = useState({
    width: 366,
    height: 768,
  });

  useEffect(() => {
    const updateSvgSize = () => {
      setSvgDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateSvgSize();

    window.addEventListener('resize', updateSvgSize);

    return () => {
      window.removeEventListener('resize', updateSvgSize);
    };
  }, []);

  const handleCameraError = () => {
    setHasAccessError(true);
  };

  const handleRetry = () => {
    setHasAccessError(false);
  };

  return (
    <div className="relative h-screen min-h-screen w-screen items-center bg-black">
      {hasAccessError ? (
        <CameraFailedError
          errorMsg={t('message')}
          errorTitle={t('title')}
          onRetry={handleRetry}
          retryCTAText={t('retryCTAText')}
        />
      ) : (
        <>
          <CameraCaptureAuto
            imageQuality={0.5}
            onCameraError={handleCameraError}
            onUpdateCapturedImage={onCaptureSuccess}
          />

          <div className="absolute left-0 top-0 z-50 h-screen w-screen">
            <CameraCircularMaskSVG
              height={svgDimensions.height}
              width={svgDimensions.width}
            />
          </div>

          <div
            className="
              absolute right-0 top-[64%] flex h-auto w-full shrink-0 items-center
              justify-center
            "
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}
